import { useBrandsModels } from '@/core/domain/people/BrandsModelsVersions/BrandsModelsVersions.hooks';
import {
  BrandsModelsRequest,
  Model,
} from '@/core/domain/people/BrandsModelsVersions/BrandsModelsVersions.types';
import { useEffect, useState } from 'react';
import { Key } from 'antd/es/table/interface';
import { ModalProps } from '@/types/ModalProps';
import { Button, Col, Modal, Row, Typography } from 'antd';
import { ColumnsType } from '../InfiniteTable';
import { CheckOutlined } from '@ant-design/icons';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import BrandsModelsFiltersTableModal from './BrandsModelsFiltersTableModal';
import BrandsModelsTableModal from './BrandsModelsTableModal';
import { useDebounce } from '@/hooks';

interface SelectedBrandsModelsProps extends ModalProps {
  finishCallback?: (models?: Model[]) => void;
  selectedBrandsModels?: Model[];
  noSelection?: boolean;
  onBrandsModelsSelect?: (model: Model) => void;
  selectedBrandIds?: number[];
}

const BrandsModelsSelectedModal: React.FC<SelectedBrandsModelsProps> = ({
  visible = false,
  selectedBrandsModels = [],
  finishCallback,
  handleVisible,
  noSelection,
  onBrandsModelsSelect,
  selectedBrandIds,
}) => {
  const [queryParams, setQueryParams] = useState<BrandsModelsRequest>({
    order: 'DESC',
  });

  const [selectedBrandsModelsKeys, setSelectedBrandsModelsKeys] = useState<
    Key[]
  >([]);

  const queryDebounce = useDebounce(queryParams);

  const { data, isFetching, hasNextPage, fetchNextPage } = useBrandsModels(
    { ...queryDebounce, ids: selectedBrandIds },
    visible,
  );

  const brandsModels = flatInfiniteData(data);

  useEffect(() => {
    if (visible) {
      setSelectedBrandsModelsKeys(
        selectedBrandsModels.map((brandsModels) => brandsModels.id),
      );
    }
  }, [visible]);

  const getSelectedBrands = (): Model[] => {
    const result = brandsModels.filter((brandsModels) => {
      return selectedBrandsModelsKeys.includes(brandsModels.id);
    });

    return result;
  };

  const handleCancel = (): void => {
    setQueryParams({
      order: 'DESC',
      ids: selectedBrandIds,
    });
    setSelectedBrandsModelsKeys([]);

    handleVisible();
  };

  const handleOk = (): void => {
    const brandsModels = getSelectedBrands();
    if (finishCallback) {
      finishCallback(brandsModels);
    }

    handleCancel();
  };

  const columns: ColumnsType<Model> = [
    {
      dataIndex: 'name',
      title: 'Modelo',
      width: 200,
    },
    {
      dataIndex: 'brand',
      title: 'Montadora',
      render: (_, row) => row.brand.name,
      width: 200,
      ellipsis: true,
    },
    {
      dataIndex: 'yearStartFabrication',
      title: 'Início de Fabricação',
      width: 135,
      ellipsis: true,
    },
    {
      dataIndex: 'yearEndFabrication',
      title: 'Término de Fabricação',
      width: 155,
      ellipsis: true,
    },
    onBrandsModelsSelect
      ? {
          dataIndex: 'actions',
          align: 'right',
          render: (_, row) => (
            <Button
              type="primary"
              onClick={() => onBrandsModelsSelect(row)}
              icon={<CheckOutlined />}
            />
          ),
          width: '20%',
        }
      : {},
  ];

  const handleBrandsModelsSelect = (selectedRowKeys: Key[]) => {
    setSelectedBrandsModelsKeys(selectedRowKeys);
  };

  return (
    <Modal
      title="Adicionar Modelos"
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={800}
    >
      <Row gutter={16}>
        <Col flex="1">
          <Typography.Title style={{ marginBottom: '24px' }} level={4}>
            Busque os Modelos
          </Typography.Title>

          <BrandsModelsFiltersTableModal
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />

          <BrandsModelsTableModal
            noSelection={noSelection}
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            columns={columns}
            models={brandsModels}
            selectedBrandsModelsKeys={selectedBrandsModelsKeys}
            handleBrandsModelsSelect={handleBrandsModelsSelect}
          />
        </Col>
      </Row>
    </Modal>
  );
};

export default BrandsModelsSelectedModal;
